import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, TextField, Typography, Paper, Divider,
  Stack, Chip, Tooltip, Button
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { addPod, updatePod, deletePod } from '../../store/slices/podsSlice';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import NumberInput from '../../components/NumberInput/NumberInput';

const PodForm: React.FC = () => {
  const navigate = useNavigate();
  const { podId } = useParams<{ podId: string }>();
  const dispatch = useAppDispatch();
  const existingPod = useAppSelector((state) =>
    state.pods.pods.find((p) => p.id === podId)
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultVelocity, setDefaultVelocity] = useState(30);
  const [avgPointsPerEngineer, setAvgPointsPerEngineer] = useState(0);

  useEffect(() => {
    if (existingPod) {
      setName(existingPod.name);
      setDescription(existingPod.description);
      setDefaultVelocity(existingPod.defaultVelocity);
      setAvgPointsPerEngineer(existingPod.avgPointsPerEngineer ?? 0);
    }
  }, [existingPod]);

  const handleSave = () => {
    if (!name.trim()) return;
    if (existingPod) {
      dispatch(updatePod({ ...existingPod, name, description, defaultVelocity, avgPointsPerEngineer }));
    } else {
      dispatch(addPod({ name, description, defaultVelocity, avgPointsPerEngineer, teamMembers: [] }));
    }
    navigate('/pod-management');
  };

  const handleDelete = () => {
    if (existingPod) {
      dispatch(deletePod(existingPod.id));
      navigate('/pod-management');
    }
  };

  const engineerCount = existingPod
    ? existingPod.teamMembers.filter((m) => m.group === 'Engineering').length
    : 0;
  const calculatedVelocity = avgPointsPerEngineer * engineerCount;
  const isMismatch = avgPointsPerEngineer > 0 && defaultVelocity !== calculatedVelocity;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {existingPod ? 'Edit Pod' : 'Create Pod'}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Stack spacing={3}>
          <TextField
            label="Pod Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            error={!name.trim()}
            helperText={!name.trim() ? 'Pod name is required' : ''}
          />
          <TextField
            label="Pod Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <Box>
            <Typography variant="body2" gutterBottom>Avg. points per engineer per sprint</Typography>
            <Box
              sx={{
                border: '1px solid', borderColor: 'divider',
                borderRadius: 1, p: 1.5,
              }}
            >
              <NumberInput
                value={avgPointsPerEngineer}
                onChange={setAvgPointsPerEngineer}
                min={0}
                max={50}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  × {engineerCount} engineer{engineerCount !== 1 ? 's' : ''} = {calculatedVelocity} pts calculated
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" gutterBottom>Engineering Velocity (story points)</Typography>
            <Box
              sx={{
                border: '1px solid', borderColor: isMismatch ? 'warning.main' : 'divider',
                borderRadius: 1, p: 1.5,
              }}
            >
              <NumberInput
                value={defaultVelocity}
                onChange={setDefaultVelocity}
                min={0}
                max={200}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                {avgPointsPerEngineer > 0 && (
                  <Typography variant="caption" color={isMismatch ? 'warning.main' : 'text.secondary'}>
                    Calculated: {calculatedVelocity} pts
                  </Typography>
                )}
                {isMismatch && (
                  <Chip label="Custom" size="small" color="warning" variant="outlined" />
                )}
                {isMismatch && (
                  <Tooltip title={`Reset to calculated value (${calculatedVelocity} pts)`}>
                    <Button size="small" onClick={() => setDefaultVelocity(calculatedVelocity)}>
                      Reset
                    </Button>
                  </Tooltip>
                )}

              </Box>
            </Box>
          </Box>
        </Stack>
        <Box sx={{ mt: 4 }}>
          <ModalActionFooter
            onSave={handleSave}
            saveLabel={existingPod ? 'Save' : 'Create'}
            saveDisabled={!name.trim()}
            onCancel={() => navigate('/pod-management')}
            onDelete={existingPod ? handleDelete : undefined}
            deleteLabel="Delete Pod"
            deleteConfirmMessage="Are you sure you want to delete this pod? This action cannot be undone."
          />
        </Box>
      </Paper>
    </Box >
  );
};

export default PodForm;
